import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    TrendingUp,
    TrendingDown,
    RefreshCw,
    Settings,
    Loader2,
    Wifi,
    WifiOff,
    BarChart3,
    Globe,
} from 'lucide-react';
import { useSnackbar } from 'notistack';
import { useCrmAllPrices } from '@/hooks/useCrmAllPrices';
import { useCrmInstruments } from '@/api/instruments/use-instruments';
import { useNavigate } from 'react-router-dom';
import { formatInstrumentPrice } from '@/utils/formatters';

interface RealTimeMarketWidgetProps {
    selectedInstruments?: string[]; // Instrument IDs to monitor
    maxInstruments?: number;
    className?: string;
    showActions?: boolean;
    refreshInterval?: number; // Refresh interval in milliseconds
}

interface MarketTrend {
    instrumentId: string;
    symbol: string;
    name: string;
    currentPrice: number;
    change: number;
    changePercent: number;
    trend: 'up' | 'down' | 'neutral';
    volume?: number;
    high24h?: number;
    low24h?: number;
}

const RealTimeMarketWidget: React.FC<RealTimeMarketWidgetProps> = ({
    selectedInstruments = [],
    maxInstruments = 8,
    className,
    showActions = true,
    refreshInterval = 10000, // 10 seconds
}) => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [isConnected, setIsConnected] = useState(true);
    const [lastUpdate, setLastUpdate] = useState(new Date());
    const [priceHistory, setPriceHistory] = useState<Record<string, number[]>>({});
    const [marketTrends, setMarketTrends] = useState<MarketTrend[]>([]);

    const { data: instrumentsResponse } = useCrmInstruments();
    const instruments = instrumentsResponse?.data || [];

    // Get instruments to monitor (selected or popular)
    const instrumentsToMonitor =
        selectedInstruments.length > 0
            ? selectedInstruments.slice(0, maxInstruments)
            : instruments.slice(0, maxInstruments).map(inst => inst.id);

    const { prices: pricesData, isLoading, error, refetch } = useCrmAllPrices(instrumentsToMonitor);

    const prices = pricesData || new Map();

    // Track price history and calculate trends
    useEffect(() => {
        if (prices && prices.size > 0) {
            setPriceHistory(prev => {
                const newHistory = { ...prev };
                prices.forEach((priceData, instrumentId) => {
                    if (priceData && typeof priceData === 'object' && 'last' in priceData) {
                        const currentPrice = priceData.last;
                        if (!newHistory[instrumentId]) {
                            newHistory[instrumentId] = [];
                        }
                        newHistory[instrumentId].push(currentPrice);
                        // Keep only last 20 prices for trend analysis
                        if (newHistory[instrumentId].length > 20) {
                            newHistory[instrumentId] = newHistory[instrumentId].slice(-20);
                        }
                    }
                });
                return newHistory;
            });

            // Calculate market trends
            const trends: MarketTrend[] = instrumentsToMonitor.map(instrumentId => {
                const instrument = instruments.find(inst => inst.id === instrumentId);
                const priceData = prices.get(instrumentId);
                const history = priceHistory[instrumentId] || [];

                const currentPrice = priceData?.last || 0;
                const previousPrice =
                    history.length > 1 ? history[history.length - 2] : currentPrice;
                const change = currentPrice - previousPrice;
                const changePercent = previousPrice > 0 ? (change / previousPrice) * 100 : 0;

                // Determine trend based on recent price movement
                let trend: 'up' | 'down' | 'neutral' = 'neutral';
                if (history.length >= 5) {
                    const recent = history.slice(-5);
                    const isIncreasing = recent.every(
                        (price, index) => index === 0 || price >= recent[index - 1]
                    );
                    const isDecreasing = recent.every(
                        (price, index) => index === 0 || price <= recent[index - 1]
                    );

                    if (isIncreasing) trend = 'up';
                    else if (isDecreasing) trend = 'down';
                }

                return {
                    instrumentId,
                    symbol: instrument?.symbol || instrumentId,
                    name: instrument?.symbol || instrumentId,
                    currentPrice,
                    change,
                    changePercent,
                    trend,
                    volume: undefined, // PriceData doesn't have volume
                    high24h: undefined, // PriceData doesn't have high
                    low24h: undefined, // PriceData doesn't have low
                };
            });

            setMarketTrends(trends);
            setLastUpdate(new Date());
            setIsConnected(true);
        }
    }, [prices, instruments, instrumentsToMonitor, priceHistory]);

    // Handle connection status
    useEffect(() => {
        if (error) {
            setIsConnected(false);
        }
    }, [error]);

    const formatPrice = (price: number, instrumentId: string) => {
        const instrument = instruments.find(inst => inst.id === instrumentId);
        return formatInstrumentPrice(price, instrument);
    };

    const formatChange = (change: number) => {
        const sign = change >= 0 ? '+' : '';
        return `${sign}${change.toFixed(2)}%`;
    };

    const getMarketSentiment = () => {
        if (marketTrends.length === 0) return 'neutral';

        const upTrends = marketTrends.filter(trend => trend.trend === 'up').length;
        const downTrends = marketTrends.filter(trend => trend.trend === 'down').length;

        if (upTrends > downTrends) return 'bullish';
        if (downTrends > upTrends) return 'bearish';
        return 'neutral';
    };

    const getTopGainers = () => {
        return marketTrends
            .filter(trend => trend.changePercent > 0)
            .sort((a, b) => b.changePercent - a.changePercent)
            .slice(0, 3);
    };

    const getTopLosers = () => {
        return marketTrends
            .filter(trend => trend.changePercent < 0)
            .sort((a, b) => a.changePercent - b.changePercent)
            .slice(0, 3);
    };

    const handleRefresh = () => {
        refetch();
        enqueueSnackbar('Market data refreshed', { variant: 'success' });
    };

    const handleViewTrading = () => {
        navigate('/trade');
    };

    const marketSentiment = getMarketSentiment();
    const topGainers = getTopGainers();
    const topLosers = getTopLosers();

    return (
        <Card className={className}>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                        <Globe className="h-5 w-5" />
                        Real-Time Market
                    </CardTitle>
                    <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                            {isConnected ? (
                                <Wifi className="h-4 w-4 text-green-500" />
                            ) : (
                                <WifiOff className="h-4 w-4 text-red-500" />
                            )}
                            <span className="text-xs text-muted-foreground">
                                {isConnected ? 'Live' : 'Offline'}
                            </span>
                        </div>
                        {showActions && (
                            <>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleRefresh}
                                    disabled={isLoading}
                                    className="h-8 w-8 p-0"
                                >
                                    <RefreshCw
                                        className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`}
                                    />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleViewTrading}
                                    className="h-8 w-8 p-0"
                                >
                                    <Settings className="h-4 w-4" />
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {isLoading && !marketTrends.length ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                ) : (
                    <>
                        {/* Connection Status */}
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">
                                Last update: {lastUpdate.toLocaleTimeString()}
                            </span>
                            <Badge variant={isConnected ? 'default' : 'destructive'}>
                                {isConnected ? 'Connected' : 'Disconnected'}
                            </Badge>
                        </div>

                        {/* Market Sentiment */}
                        <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                            <div className="flex items-center gap-2">
                                <BarChart3 className="h-4 w-4" />
                                <span className="text-sm font-medium">Market Sentiment</span>
                            </div>
                            <Badge
                                variant="outline"
                                className={
                                    marketSentiment === 'bullish'
                                        ? 'bg-green-500/20 text-green-600 border-green-500/30'
                                        : marketSentiment === 'bearish'
                                          ? 'bg-red-500/20 text-red-600 border-red-500/30'
                                          : 'bg-gray-500/20 text-gray-600 border-gray-500/30'
                                }
                            >
                                {marketSentiment === 'bullish'
                                    ? 'Bullish'
                                    : marketSentiment === 'bearish'
                                      ? 'Bearish'
                                      : 'Neutral'}
                            </Badge>
                        </div>

                        {/* Top Gainers */}
                        {topGainers.length > 0 && (
                            <div className="space-y-2">
                                <h4 className="text-sm font-medium text-muted-foreground">
                                    Top Gainers
                                </h4>
                                <div className="space-y-2">
                                    {topGainers.map(trend => (
                                        <div
                                            key={trend.instrumentId}
                                            className="flex items-center justify-between p-2 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800"
                                        >
                                            <div className="flex items-center gap-2">
                                                <TrendingUp className="h-4 w-4 text-green-500" />
                                                <span className="font-semibold text-sm">
                                                    {trend.symbol}
                                                </span>
                                            </div>
                                            <div className="text-right">
                                                <div className="font-mono text-sm font-bold text-green-600">
                                                    {formatPrice(
                                                        trend.currentPrice,
                                                        trend.instrumentId
                                                    )}
                                                </div>
                                                <div className="text-xs font-medium text-green-600">
                                                    {formatChange(trend.changePercent)}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Top Losers */}
                        {topLosers.length > 0 && (
                            <div className="space-y-2">
                                <h4 className="text-sm font-medium text-muted-foreground">
                                    Top Losers
                                </h4>
                                <div className="space-y-2">
                                    {topLosers.map(trend => (
                                        <div
                                            key={trend.instrumentId}
                                            className="flex items-center justify-between p-2 bg-red-50 dark:bg-red-950/20 rounded-lg border border-red-200 dark:border-red-800"
                                        >
                                            <div className="flex items-center gap-2">
                                                <TrendingDown className="h-4 w-4 text-red-500" />
                                                <span className="font-semibold text-sm">
                                                    {trend.symbol}
                                                </span>
                                            </div>
                                            <div className="text-right">
                                                <div className="font-mono text-sm font-bold text-red-600">
                                                    {formatPrice(
                                                        trend.currentPrice,
                                                        trend.instrumentId
                                                    )}
                                                </div>
                                                <div className="text-xs font-medium text-red-600">
                                                    {formatChange(trend.changePercent)}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* All Instruments */}
                        <div className="space-y-2">
                            <h4 className="text-sm font-medium text-muted-foreground">
                                All Instruments
                            </h4>
                            <div className="space-y-2">
                                {marketTrends.map(trend => (
                                    <div
                                        key={trend.instrumentId}
                                        className="flex items-center justify-between p-2 bg-muted rounded-lg hover:bg-muted/80 transition-colors"
                                    >
                                        <div className="flex items-center gap-2">
                                            {trend.trend === 'up' && (
                                                <TrendingUp className="h-4 w-4 text-green-500" />
                                            )}
                                            {trend.trend === 'down' && (
                                                <TrendingDown className="h-4 w-4 text-red-500" />
                                            )}
                                            {trend.trend === 'neutral' && (
                                                <div className="h-4 w-4 rounded-full bg-gray-400" />
                                            )}
                                            <div>
                                                <div className="font-semibold text-sm">
                                                    {trend.symbol}
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    {trend.name}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-mono text-sm font-bold">
                                                {formatPrice(trend.currentPrice)}
                                            </div>
                                            <div
                                                className={`text-xs font-medium ${
                                                    trend.changePercent >= 0
                                                        ? 'text-green-600'
                                                        : 'text-red-600'
                                                }`}
                                            >
                                                {formatChange(trend.changePercent)}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* No Data State */}
                        {marketTrends.length === 0 && !isLoading && (
                            <div className="text-center text-muted-foreground py-8">
                                <Globe className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                <p className="text-sm">No market data available</p>
                                <p className="text-xs">Select instruments to monitor</p>
                            </div>
                        )}

                        {/* Error State */}
                        {error && (
                            <div className="text-center text-red-500 py-4">
                                <WifiOff className="h-8 w-8 mx-auto mb-2" />
                                <p className="text-sm">Failed to fetch market data</p>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleRefresh}
                                    className="mt-2"
                                >
                                    Retry
                                </Button>
                            </div>
                        )}

                        {/* Refresh Info */}
                        <div className="text-center text-xs text-muted-foreground">
                            Updates every {refreshInterval / 1000}s
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default RealTimeMarketWidget;
